import { Alert, Button, Table, message } from "antd";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getQueryParam, updateQueryParam } from "shared/lib";
import {
  ActiveDynamicTrackerFieldFilter,
  DynamicTrackerFieldSelection,
} from "shared/types";
import {
  MaterialCategoriesItem,
  useGetMaterialItemsQuery,
  useMassMaterialItemsUpdatedMutation,
  useXlsExportMaterialItemsMutation,
} from "shared/api";
import {
  useDateFilters,
  useDebouncedTableFilter,
  useGenerateItemViewColumns,
  useItemViewMassUpdate,
  useModal,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import { RenderClearFiltersButton } from "shared/ui";
import { MassUpdateModal } from "./MassUpdateModal";
import { ParentItemsModal } from "./ParentItemsModal";

interface ItemViewTableProps {
  selectedMaterialCategoryItem: MaterialCategoriesItem | null;
  globalSerialNumber: string;
  setGlobalSerialNumber: (value: string) => void;
  handleSelectMaterialCategory: (materialCategoryId: number | null) => void;
  hasEditPermission?: boolean;
  onExportAll?: (ids: number[]) => void;
  dynamicTrackerFieldsFilter?: string;
  clearDynamicTrackerFieldsFilter?: () => void;
  dynamicFilters?: Record<string, ActiveDynamicTrackerFieldFilter>;
  applyDynamicFilter?: (filter: ActiveDynamicTrackerFieldFilter) => void;
  clearDynamicFilter?: (section: string, field: string) => void;
}

const ItemViewTable: FC<ItemViewTableProps> = ({
  selectedMaterialCategoryItem,
  globalSerialNumber,
  setGlobalSerialNumber,
  handleSelectMaterialCategory,
  hasEditPermission,
  onExportAll,
  dynamicTrackerFieldsFilter,
  clearDynamicTrackerFieldsFilter,
  dynamicFilters,
  applyDynamicFilter,
  clearDynamicFilter,
}) => {
  const { t } = useTranslation();

  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    usePagination();

  const [massMaterialItemsUpdated] = useMassMaterialItemsUpdatedMutation();

  const { ordering, changeSort } = useTableSorting();

  const {
    debouncedValue: debouncedSerialNumberValue,
    handleFilterChange: handleSerialNumberValue,
    value: serialNumberValue,
    clearFilterValue: clearSerialNumberValue,
  } = useDebouncedTableFilter("SerialNumber", 400);

  const {
    dateGreater,
    dateLesser,
    clearDates,
    handleDateGreater,
    handleDateLesser,
    isDataFilterActive,
  } = useDateFilters();

  const {
    dateGreater: dateLastScanGreater,
    dateLesser: dateLastScanLesser,
    clearDates: clearLastScanDates,
    handleDateGreater: handleLastScanGreater,
    handleDateLesser: handleLastScanLesser,
    isDataFilterActive: isLastScanFilterActive,
  } = useDateFilters();

  const statusParam = getQueryParam("status");
  const situationParam = getQueryParam("situation");

  const [selectedStatus, setSelectedStatus] = useState<CheckboxValueType[]>(
    statusParam ? statusParam.split(",") : []
  );
  const [selectedSituation, setSelectedSituation] = useState<
    CheckboxValueType[]
  >(situationParam ? situationParam.split(",") : []);

  const [isParentsModalOpen, setIsParentsModalOpen] = useState(false);
  const [parentSourceId, setParentSourceId] = useState<number | null>(null);

  const openParentsModal = (id: number) => {
    setParentSourceId(id);
    setIsParentsModalOpen(true);
  };

  const handleStatusChange = (checkedValues: CheckboxValueType[]) => {
    setSelectedStatus(checkedValues);
    updateQueryParam("status", checkedValues.join(","));
  };

  const handleSituationChange = (checkedValues: CheckboxValueType[]) => {
    setSelectedSituation(checkedValues);
    updateQueryParam("situation", checkedValues.join(","));
  };

  const {
    debouncedValue: debouncedMaterialNameValue,
    handleFilterChange: handleMaterialNameValue,
    value: materialNameValue,
    clearFilterValue: clearMaterialNameValue,
  } = useDebouncedTableFilter("MaterialName", 400);

  const {
    debouncedValue: debouncedOrderValue,
    handleFilterChange: handleOrderValue,
    value: orderValue,
    clearFilterValue: cleaOrderValue,
  } = useDebouncedTableFilter("Order", 400);

  const {
    debouncedValue: debouncedParentSerialValue,
    handleFilterChange: handleParentSerialValue,
    value: parentSerialValue,
    clearFilterValue: clearParentSerialValue,
  } = useDebouncedTableFilter("ParentSerial", 400);

  const isVisibleClearFiltersButton =
    isDataFilterActive ||
    !!serialNumberValue ||
    !!selectedStatus.length ||
    !!globalSerialNumber ||
    !!selectedMaterialCategoryItem ||
    !!orderValue ||
    !!parentSerialValue ||
    !!isLastScanFilterActive ||
    !!dynamicTrackerFieldsFilter;

  const clearAllFilters = () => {
    clearSerialNumberValue();
    clearDates();
    setSelectedStatus([]);
    clearMaterialNameValue();
    setSelectedSituation([]);
    clearLastScanDates();
    setGlobalSerialNumber("");
    handleSelectMaterialCategory(null);
    cleaOrderValue();
    clearParentSerialValue();
    clearDynamicTrackerFieldsFilter?.();

    updateQueryParam("status");
    updateQueryParam("situation");
  };

  const {
    data: materialItems,
    isLoading: isLoadingMaterialItems,
    isFetching: isFetchingMaterialItems,
  } = useGetMaterialItemsQuery(
    {
      material_stock_batch__material__material_category:
        globalSerialNumber || selectedMaterialCategoryItem === null
          ? undefined
          : Number(selectedMaterialCategoryItem?.id),
      material_stock_batch__material__name__icontains:
        debouncedMaterialNameValue,
      offset,
      limit,
      ordering:
        selectedMaterialCategoryItem === null
          ? ordering || "-timestamp"
          : ordering,
      expand:
        "moved_to_order,material_stock_batch.material.material_category,material_stock_batch.material,order_production_set.order_process_step_tracker.order",
      serial_number__icontains:
        globalSerialNumber || debouncedSerialNumberValue,
      timestamp__gte: dateGreater ?? undefined,
      timestamp__lte: dateLesser ?? undefined,
      last_timestamp_scanned__gte: dateLastScanGreater ?? undefined,
      last_timestamp_scanned__lte: dateLastScanLesser ?? undefined,
      status__in: selectedStatus as string[],
      situation: selectedSituation as string[],
      order_production_set__order_process_step_tracker__order__order_code:
        debouncedOrderValue,
      parent_material_item_serial_number: debouncedParentSerialValue,
      dynamic_tracker_fields: dynamicTrackerFieldsFilter,
    },
    { skip: selectedMaterialCategoryItem === undefined && !globalSerialNumber }
  );

  // Dynamic columns + their per-column filters are derived from the API
  // response. The last non-empty set is kept so the columns don't disappear
  // when a filter returns no rows.
  const [dynamicFieldEntries, setDynamicFieldEntries] = useState<
    DynamicTrackerFieldSelection[]
  >([]);

  const resolvedDynamicFieldEntries = useMemo(() => {
    const results = materialItems?.results;
    if (!results?.length) return null;

    const map = new Map<string, DynamicTrackerFieldSelection>();
    results.forEach((item) => {
      const dynamicData = item?.dynamic_tracker_fields;
      if (!dynamicData || typeof dynamicData !== "object") return;

      Object.keys(dynamicData).forEach((section) => {
        const fields = dynamicData[section];
        if (!fields || typeof fields !== "object") return;

        Object.keys(fields).forEach((field) => {
          map.set(`${section}.${field}`, { section, field });
        });
      });
    });

    return Array.from(map.values());
  }, [materialItems]);

  useEffect(() => {
    if (!resolvedDynamicFieldEntries) return;
    setDynamicFieldEntries(resolvedDynamicFieldEntries);
  }, [resolvedDynamicFieldEntries]);

  const columns = useGenerateItemViewColumns({
    t,
    changeSort,
    // Serial Number Filtering
    handleSerialNumberValue,
    serialNumberValue,
    clearSerialNumberValue,
    // Timestamp filter
    handleDateGreater,
    handleDateLesser,
    isDataFilterActive,
    dynamicFieldEntries,
    dynamicFilters: dynamicFilters ?? {},
    onApplyDynamicFilter: applyDynamicFilter ?? (() => undefined),
    onClearDynamicFilter: clearDynamicFilter ?? (() => undefined),
    // status filter
    handleStatusChange,
    selectedStatus,
    // material name filter
    handleMaterialNameValue,
    materialNameValue,
    clearMaterialNameValue,
    // situation filter
    handleSituationChange,
    selectedSituation,
    hasEditPermission,
    // order
    handleOrderValue,
    orderValue,
    cleaOrderValue,
    // parent serial
    handleParentSerialValue,
    parentSerialValue,
    clearParentSerialValue,
    // scanner
    handleLastScanGreater,
    handleLastScanLesser,
    isLastScanFilterActive,
    onShowParents: openParentsModal,
  });

  const updatedOffsets = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (isFetchingMaterialItems || !materialItems?.results?.length) return;

    if (updatedOffsets.current.has(offset)) return;

    const itemsToUpdate = materialItems.results.filter((item) => {
      const remaining = Number(item.calc_total_remaining_quantity);
      return (
        !Number.isNaN(remaining) && remaining <= 200 && item.situation !== "US"
      );
    });

    if (!itemsToUpdate.length) return;

    updatedOffsets.current.add(offset);

    massMaterialItemsUpdated({
      material_items: itemsToUpdate.map((item) => item.id),
      situation: "US",
    }).unwrap();
  }, [materialItems, offset, isFetchingMaterialItems]);

  useEffect(() => {
    if (onExportAll) {
      const ids = materialItems?.results?.length
        ? materialItems.results.map((item) => item.id)
        : [];
      onExportAll(ids);
    }
  }, [materialItems, onExportAll]);

  const { selectedKeys, rowSelection } = useItemViewMassUpdate({
    materialItems,
  });

  const [isVisibleMassUpdateModal, showMassUpdateModal, hideMassUpdateModel] =
    useModal();

  const [xlsExport, { isLoading: isExporting }] =
    useXlsExportMaterialItemsMutation();

  const handleExportXLS = async () => {
    if (!selectedKeys.length) return;

    try {
      const blob = await xlsExport({ ids: selectedKeys as number[] }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Export error");
    }
  };

  if (selectedMaterialCategoryItem === undefined && !globalSerialNumber) {
    return (
      <Alert
        showIcon
        type="info"
        message={t("itemView.selectMaterialCategoryToShowTable")}
        className="!mt-4"
      />
    );
  }

  useEffect(() => {
    if (selectedMaterialCategoryItem === null) {
      setPageSize(100);
    } else {
      setPageSize(10);
    }
  }, [selectedMaterialCategoryItem]);

  return (
    <>
      {isVisibleClearFiltersButton && (
        <RenderClearFiltersButton onClick={clearAllFilters} />
      )}

      {selectedKeys.length && hasEditPermission ? (
        <div className="flex gap-3">
          <Button onClick={showMassUpdateModal}>
            {t("itemView.massUpdate")}
          </Button>
          <Button loading={isExporting} onClick={handleExportXLS}>
            {t("Export XLS")}
          </Button>
        </div>
      ) : null}

      <Table
        loading={isLoadingMaterialItems || isFetchingMaterialItems}
        columns={columns}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={materialItems?.results}
        rowKey={(obj) => obj.id!}
        pagination={{
          pageSize,
          pageSizeOptions: ["10", "20", "50", "100", "1000"],
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: materialItems?.count,
        }}
        className="!mt-4"
      />

      <MassUpdateModal
        isOpen={isVisibleMassUpdateModal}
        onClose={hideMassUpdateModel}
        selectedIds={selectedKeys}
      />

      <ParentItemsModal
        open={isParentsModalOpen}
        onClose={() => {
          setIsParentsModalOpen(false);
          setParentSourceId(null);
        }}
        parentSourceId={parentSourceId}
        columns={columns}
      />
    </>
  );
};

export { ItemViewTable };
